import { inject, Injectable } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  ResolveEnd,
  ResolveStart,
  Router,
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, merge } from 'rxjs';
import { filter, map, skip } from 'rxjs/operators';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class RouterService {
  loading$: Observable<boolean>;
  private showLoading$: Observable<boolean>;
  private hideLoading$: Observable<boolean>;

  private navigationTarget: string | undefined;
  private lastSuccessfulRoute: string | undefined;
  private pendingPreviousRoute: string | undefined;

  private router = inject(Router);

  constructor() {
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart &&
            (this.pendingPreviousRoute === undefined ||
              event.url !== this.navigationTarget),
        ),
        skip(1), //skip initial loading/router navigation from / to url
        untilDestroyed(this),
      )
      .subscribe((event: NavigationStart) => {
        const previousUrl = this.router.lastSuccessfulNavigation()?.finalUrl;
        this.pendingPreviousRoute = previousUrl
          ? previousUrl.toString()
          : undefined;
        this.navigationTarget = event.url;
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (this.pendingPreviousRoute !== undefined) {
          this.lastSuccessfulRoute = this.pendingPreviousRoute;
        }
        this.pendingPreviousRoute = undefined;
        this.navigationTarget = undefined;
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationCancel),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.pendingPreviousRoute = undefined;
      });

    this.showLoading$ = this.router.events.pipe(
      filter((event) => event instanceof ResolveStart),
      map(() => true)
    );
    this.hideLoading$ = this.router.events.pipe(
      filter((event) => event instanceof ResolveEnd),
      map(() => false)
    );
    this.loading$ = merge(this.showLoading$, this.hideLoading$);
  }

  public continueNavigationTarget(fallbackURL: string = '/dashboard') {
    const url = this.lastSuccessfulRoute ?? this.navigationTarget ?? fallbackURL;
    void this.router.navigateByUrl(url);
  }
}
